import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertDialog,
  Button,
  DatePicker,
  FormField,
  Input,
  Modal,
  NumberInput,
  ScrollArea,
  Switch,
  Textarea,
  useToast,
} from '@ntconsult/aluma-react';
import {
  type Ref,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, type DefaultValues, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAllocationDefaults } from '../hooks/useAllocationDefaults';
import type { SelectedOption } from '../hooks/useAllocationFilters';
import { useContracts } from '../hooks/useContracts';
import { useProfessionals } from '../hooks/useProfessionals';
import { useProjects } from '../hooks/useProjects';
import { useSalesProfiles } from '../hooks/useSalesProfiles';
import { useSaveAllocation } from '../hooks/useSaveAllocation';
import { useValidateAllocation } from '../hooks/useValidateAllocation';
import type { Allocation, AllocationBody } from '../types/Allocation';
import type { Warning } from '../types/ApiError';
import type { ProjectOption } from '../types/Project';
import type { SalesProfileOption } from '../types/SalesProfile';
import type { TagOption } from '../types/Tag';
import { parseApiDate, toApiDate } from '../utils/data';
import { i18nKeyForError, parseApiError } from '../utils/errors';
import { contractLabel, professionalLabel } from '../utils/labels';
import {
  DEFAULT_SOLD_HOURS,
  decimalInputFormat,
  formatMoney,
  previewAllocatedCost,
  previewGrossRevenue,
  previewNetRevenue,
} from '../utils/money';
import { noticeEndDate } from '../utils/poolNotice';
import {
  type AllocationFormValues,
  allocationFormSchema,
  NOTES_MAX_LENGTH,
} from './AllocationForm.schema';
import { SalesProfileSelect } from './SalesProfileSelect';
import { SearchCombobox } from './SearchCombobox';
import { TagsCombobox } from './TagsCombobox';

export type EditAllocationTarget = {
  allocation: Allocation;
  professional: { id: number; registrationNumber: number | null; name: string };
};

type AllocationModalProps = {
  open: boolean;
  onClose: () => void;
  /** `null` = nova alocação. */
  target: EditAllocationTarget | null;
  today: string;
  readOnly?: boolean;
};

function formFieldFor(apiField: string): keyof AllocationFormValues | null {
  switch (apiField) {
    case 'professionalId':
      return 'professional';
    case 'projectId':
      return 'project';
    case 'allocatedPercentage':
    case 'billable':
    case 'startDate':
    case 'endDate':
    case 'costShareBr':
    case 'costShareUs':
    case 'notes':
    case 'fimConfirmado':
    case 'tagIds':
    case 'notified':
    case 'noticeDate':
    case 'noticeDays':
    case 'salesProfileId':
    case 'monthlyCost':
    case 'hourlyRate':
    case 'soldHours':
      return apiField;
    default:
      return null;
  }
}

function initialValues(
  target: EditAllocationTarget | null,
  today: string,
): DefaultValues<AllocationFormValues> {
  if (!target) {
    return {
      professional: undefined,
      contract: undefined,
      project: undefined,
      allocatedPercentage: 100,
      billable: true,
      startDate: parseApiDate(today) as Date,
      endDate: null,
      costShareBr: 100,
      costShareUs: 0,
      // `''`, não `undefined`: é o que o `<textarea>` registrado devolve; com
      // `undefined` o form nasceria "alterado" e fechar pediria confirmação.
      notes: '',
      fimConfirmado: false,
      tagIds: [],
      notified: false,
      noticeDate: null,
      noticeDays: 0,
      salesProfileId: null,
      monthlyCost: null,
      hourlyRate: 0,
      soldHours: 0,
      costCurrency: null,
      billingCurrency: null,
    };
  }
  const { allocation, professional } = target;
  const projectAsOption: ProjectOption = {
    id: allocation.project.id,
    axisCode: allocation.project.axisCode,
    name: allocation.project.name,
    contract: allocation.contract,
    client: allocation.client,
    unit: allocation.unit,
    isPool: allocation.project.isPool,
    isInternal: allocation.project.isInternal,
  };
  return {
    professional: {
      id: professional.id,
      name: professionalLabel(professional),
    },
    contract: { id: allocation.contract.id, name: contractLabel(allocation.contract) },
    project: projectAsOption,
    allocatedPercentage: allocation.allocatedPercentage,
    billable: allocation.billable,
    startDate: parseApiDate(allocation.startDate) as Date,
    endDate: parseApiDate(allocation.endDate),
    costShareBr: allocation.costShareBr,
    costShareUs: allocation.costShareUs,
    notes: allocation.notes ?? '',
    fimConfirmado: allocation.fimConfirmado,
    tagIds: allocation.tags.map((tag) => tag.id),
    notified: allocation.notified,
    noticeDate: parseApiDate(allocation.noticeDate),
    noticeDays: allocation.noticeDays,
    salesProfileId: allocation.salesProfile?.id ?? null,
    monthlyCost: allocation.monthlyCost,
    hourlyRate: allocation.hourlyRate ?? 0,
    soldHours: allocation.soldHours ?? 0,
    costCurrency: allocation.costCurrency,
    billingCurrency: allocation.billingCurrency,
  };
}

/** Contratos que aceitam alocação nova: o legado esconde os encerrados. */
function useAllocatableContracts(search: string) {
  return useContracts(search, { allocatable: true });
}

/** Pool ou interno não fatura (mesma regra do back e do `AlocacaoForm.php`). */
function projectBills(project: Pick<ProjectOption, 'isPool' | 'isInternal'> | undefined): boolean {
  return !project?.isPool && !project?.isInternal;
}

/** Campos de valor que vão no corpo: os customizáveis; custo alocado e faturamento o back recalcula. */
function valuesBody(
  values: Partial<
    Pick<AllocationFormValues, 'salesProfileId' | 'monthlyCost' | 'hourlyRate' | 'soldHours'>
  >,
) {
  return {
    salesProfileId: values.salesProfileId ?? null,
    monthlyCost: values.monthlyCost ?? undefined,
    hourlyRate: values.hourlyRate ?? 0,
    soldHours: values.soldHours ?? 0,
  };
}

/** Colunas das tags na linha 1 (grade de 4); literais para o Tailwind achar as classes. */
const TAGS_COL_SPAN: Record<number, string> = {
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
};

/** Aviso do pool no corpo: fora de projeto de pool vai vazio (o back também o limpa). */
function noticeBody(
  values: Partial<Pick<AllocationFormValues, 'notified' | 'noticeDate' | 'noticeDays'>>,
  project: Pick<ProjectOption, 'isPool'> | undefined,
) {
  if (!project?.isPool || !values.notified) {
    return { notified: false, noticeDate: null, noticeDays: 0 };
  }
  return {
    notified: true,
    noticeDate: toApiDate(values.noticeDate ?? null),
    noticeDays: values.noticeDays ?? 0,
  };
}

/** O modal consulta o form só ao fechar, sem espelhar `isDirty` num estado. */
type FormBodyHandle = { isDirty: boolean };

type FormBodyProps = Omit<AllocationModalProps, 'open'> & {
  /** Fechar pelo "Cancelar": pode pedir confirmação se houver alteração. */
  onCancel: () => void;
  ref: Ref<FormBodyHandle>;
};

function FormBody({ target, today, onClose, onCancel, ref, readOnly = false }: FormBodyProps) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const { toast } = useToast();
  const saveMutation = useSaveAllocation();
  const [submitWarnings, setSubmitWarnings] = useState<Warning[]>([]);
  /**
   * Objetos completos (id/nome/cor), não só ids — a lista de tags é buscada
   * sob demanda (`TagsCombobox`), então uma tag já selecionada (editar uma
   * alocação) pode não estar entre os resultados da busca atual e precisa do
   * nome/cor guardados aqui pra exibir o chip corretamente.
   */
  const [selectedTags, setSelectedTags] = useState<TagOption[]>(
    () => target?.allocation.tags ?? [],
  );

  const fetchDefaults = useAllocationDefaults();

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<AllocationFormValues>({
    resolver: zodResolver(allocationFormSchema),
    mode: 'onChange',
    defaultValues: initialValues(target, today),
  });

  const values = useWatch({ control });

  useImperativeHandle(ref, () => ({ isDirty }), [isDirty]);

  const validationBody = useMemo((): (AllocationBody & { id?: number }) | null => {
    if (readOnly) return null;
    const professional = values.professional;
    const project = values.project;
    if (!professional?.id || !project?.id || !values.startDate) return null;
    return {
      id: target?.allocation.id,
      professionalId: professional.id,
      projectId: project.id,
      allocatedPercentage: values.allocatedPercentage ?? 0,
      billable: values.billable ?? true,
      startDate: toApiDate(values.startDate) as string,
      endDate: toApiDate(values.endDate ?? null),
      costShareBr: values.costShareBr ?? 0,
      costShareUs: values.costShareUs ?? 0,
      notes: values.notes || null,
      fimConfirmado: values.fimConfirmado ?? false,
      tagIds: values.tagIds ?? [],
      ...noticeBody(values, project as ProjectOption),
      ...valuesBody(values),
    };
  }, [values, target, readOnly]);

  const { data: validation } = useValidateAllocation(validationBody);
  const blockers = validation?.blockers ?? [];
  const warnings = submitWarnings.length > 0 ? submitWarnings : (validation?.warnings ?? []);

  function serverErrorFor(apiField: string): string | undefined {
    const blocker = blockers.find((b) => b.fields?.includes(apiField));
    return blocker ? t(i18nKeyForError(blocker.code) as never) : undefined;
  }

  /** Seleção vazia vem do schema (`required`); recusa do back (422) já chega traduzida. */
  function selectionError(message: string | undefined): string | undefined {
    if (!message) return undefined;
    return message === 'required' ? t('common:errors.required') : message;
  }

  const professionalError =
    serverErrorFor('professionalId') ?? selectionError(errors.professional?.message);
  const projectError = serverErrorFor('projectId') ?? selectionError(errors.project?.message);

  const onSubmit = handleSubmit((formValues) => {
    setSubmitWarnings([]);
    const body: AllocationBody = {
      professionalId: formValues.professional.id,
      projectId: formValues.project.id,
      allocatedPercentage: formValues.allocatedPercentage,
      billable: formValues.billable,
      startDate: toApiDate(formValues.startDate) as string,
      endDate: toApiDate(formValues.endDate),
      costShareBr: formValues.costShareBr,
      costShareUs: formValues.costShareUs,
      notes: formValues.notes || null,
      confirmWarnings: warnings.length > 0,
      fimConfirmado: formValues.fimConfirmado,
      tagIds: formValues.tagIds,
      ...noticeBody(formValues, formValues.project),
      ...valuesBody(formValues),
    };
    saveMutation.mutate(
      { id: target?.allocation.id, body },
      {
        onSuccess: () => {
          toast.success({
            title: t(target ? 'alocacao:toasts.atualizarSucesso' : 'alocacao:toasts.criarSucesso'),
          });
          onClose();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          if (!apiError) {
            toast.danger({ title: t('alocacao:toasts.erro') });
            return;
          }
          if (apiError.code === 'UNCONFIRMED_WARNINGS') {
            setSubmitWarnings(apiError.warnings ?? []);
            return;
          }
          if (apiError.statusCode === 422 && apiError.fields) {
            for (const apiField of apiError.fields) {
              const field = formFieldFor(apiField);
              if (field) setError(field, { message: t(i18nKeyForError(apiError.code) as never) });
            }
            return;
          }
          toast.danger({ title: t(i18nKeyForError(apiError.code) as never) });
        },
      },
    );
  });

  const selectedContractId = values.contract?.id;
  const selectedProject = values.project as ProjectOption | undefined;
  const canSave = isValid && blockers.length === 0 && !saveMutation.isPending;
  const billsProject = projectBills(selectedProject);
  const { data: salesProfilesPage } = useSalesProfiles(selectedContractId);
  const salesProfiles = salesProfilesPage?.items ?? [];
  const selectedProfile = salesProfiles.find((profile) => profile.id === values.salesProfileId);
  const costIso = values.costCurrency?.iso;
  const billingIso = values.billingCurrency?.iso;
  const decimalFormat = decimalInputFormat(i18n.language);
  const valueError = (field: 'monthlyCost' | 'hourlyRate' | 'soldHours') =>
    serverErrorFor(field) ??
    (errors[field]?.message ? t('alocacao:erros.invalidFinancialValue') : undefined);

  /**
   * Custo, moeda e rateio do contrato do profissional (legado
   * `onChageProfissional`). Falhar aqui não bloqueia nada: sem contrato ativo
   * ou fora do escopo, a pré-validação já mostra o bloqueio.
   */
  async function applyProfessionalDefaults(professionalId: number) {
    try {
      const defaults = await fetchDefaults({ professionalId });
      if (getValues('professional')?.id !== professionalId) return; // trocou de novo enquanto buscava
      const currency = defaults.costCurrency;
      setValue('costCurrency', currency ? { id: currency.id, iso: currency.iso } : null);
      setValue('monthlyCost', defaults.monthlyCost, { shouldValidate: true });
      setValue('costShareBr', defaults.costShareBr, { shouldValidate: true });
      setValue('costShareUs', defaults.costShareUs, { shouldValidate: true });
    } catch {
      // ver JSDoc
    }
  }

  // Hook repassado ao `SearchCombobox`, que o chama no topo do próprio render
  // — sempre exatamente um `useQuery`, só o `contractId` varia.
  const useProjectsOfContract = useCallback(
    // biome-ignore lint/correctness/useHookAtTopLevel: ver comentário acima
    (search: string) => useProjects(search, selectedContractId),
    [selectedContractId],
  );

  /**
   * Outro contrato recarrega projetos e perfis (legado
   * `onChangeContratoCliente`): o projeto e o perfil escolhidos eram do
   * contrato anterior, então saem, junto com o preço que vinha do perfil.
   */
  function applyContract(contract: SelectedOption | null, previous: SelectedOption | undefined) {
    if (contract?.id === previous?.id) return;
    setValue('project', null as unknown as ProjectOption, { shouldValidate: true });
    setValue('salesProfileId', null);
    setValue('billingCurrency', null);
    setValue('hourlyRate', 0, { shouldValidate: true });
    setValue('soldHours', 0, { shouldValidate: true });
  }

  /** Pool/interno não fatura (legado `onChangeProjeto`); fora do pool não há aviso. */
  function applyProjectRules(project: ProjectOption) {
    if (!projectBills(project)) {
      setValue('billable', false);
      setValue('hourlyRate', 0, { shouldValidate: true });
      setValue('soldHours', 0, { shouldValidate: true });
    }
    if (!project.isPool) {
      setValue('notified', false);
      applyNotice();
    }
  }

  /**
   * Aviso do pool (T3), por evento como o resto do form (chamado depois do
   * `field.onChange`): sem aviso, data e dias ficam vazios; com a data, o fim
   * vira data do aviso + dias.
   */
  function applyNotice() {
    if (!getValues('notified')) {
      setValue('noticeDate', null, { shouldValidate: true });
      setValue('noticeDays', 0, { shouldValidate: true });
      return;
    }
    const noticeDate = getValues('noticeDate');
    if (noticeDate) {
      setValue('endDate', noticeEndDate(noticeDate, getValues('noticeDays') ?? 0), {
        shouldValidate: true,
      });
    }
  }

  /** Preço e moeda do perfil, 168 h quando há preço (legado `onChangePerfil`). */
  function applySalesProfile(profile: SalesProfileOption | null) {
    const hourlyRate = profile && billsProject ? profile.hourlyRate : 0;
    setValue('salesProfileId', profile?.id ?? null, { shouldValidate: true });
    setValue('billingCurrency', profile?.currency ?? null);
    setValue('hourlyRate', hourlyRate, { shouldValidate: true });
    setValue('soldHours', hourlyRate > 0 ? DEFAULT_SOLD_HOURS : 0, { shouldValidate: true });
  }

  return (
    <form onSubmit={onSubmit} className="contents" noValidate>
      <Modal.Body className="flex min-h-0 flex-col overflow-hidden px-0 py-0">
        <ScrollArea className="min-h-0 flex-1 px-6 py-3">
          <div className="flex flex-col gap-4 pr-2">
            {blockers.length > 0 && (
              <Alert variant="danger" title={t('alocacao:form.bloqueioTitulo')}>
                <ul className="list-disc pl-4">
                  {blockers.map((b) => (
                    <li key={b.code}>{t(i18nKeyForError(b.code) as never)}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {blockers.length === 0 && warnings.length > 0 && (
              <Alert variant="warning" title={t('alocacao:form.avisoTitulo')}>
                <ul className="list-disc pl-4">
                  {warnings.map((w) => (
                    <li key={w.code}>
                      {t(i18nKeyForError(w.code) as never, {
                        total: (w.data as { maxTotalPercentage?: number } | undefined)
                          ?.maxTotalPercentage,
                      })}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Linha 1 — Id · Tags · Faturando (mesma grade do `AlocacaoForm.php`). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {target && (
                <FormField label={t('alocacao:form.id')}>
                  <Input disabled value={String(target.allocation.id)} />
                </FormField>
              )}
              <FormField
                id="alocacao-tags"
                label={t('alocacao:form.tags')}
                className={TAGS_COL_SPAN[(target ? 2 : 3) + (billsProject ? 0 : 1)]}
              >
                <Controller
                  control={control}
                  name="tagIds"
                  render={({ field }) => (
                    <TagsCombobox
                      id="alocacao-tags"
                      label={t('alocacao:form.tags')}
                      value={selectedTags}
                      onChange={(next) => {
                        setSelectedTags(next);
                        field.onChange(next.map((tag) => tag.id));
                      }}
                      disabled={readOnly}
                      placeholder={t('alocacao:form.tagsPlaceholder')}
                      searchPlaceholder={t('alocacao:form.tagsBuscarPlaceholder')}
                      emptyLabel={t('alocacao:form.tagsSemResultado')}
                      removeChipLabel={(name) => t('alocacao:form.tagsRemover', { nome: name })}
                    />
                  )}
                />
              </FormField>
              {billsProject && (
                <FormField id="alocacao-billable" label={t('alocacao:form.faturando')}>
                  <Controller
                    control={control}
                    name="billable"
                    render={({ field }) => (
                      <Switch
                        id="alocacao-billable"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={readOnly}
                      />
                    )}
                  />
                </FormField>
              )}
            </div>

            {/* Linha 2 — Profissional · Contrato. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                id="alocacao-professional"
                label={t('alocacao:form.profissional')}
                error={professionalError}
                required
              >
                <Controller
                  control={control}
                  name="professional"
                  render={({ field }) => (
                    <SearchCombobox
                      id="alocacao-professional"
                      placeholder={t('alocacao:form.profissionalPlaceholder')}
                      value={field.value ?? null}
                      onChange={(option, item) => {
                        field.onChange(option);
                        if (item) void applyProfessionalDefaults(item.id);
                      }}
                      useSearch={useProfessionals}
                      getLabel={professionalLabel}
                      invalid={!!professionalError}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="alocacao-contract"
                label={t('alocacao:form.contrato')}
                error={errors.contract?.message ? t('common:errors.required') : undefined}
                required
              >
                <Controller
                  control={control}
                  name="contract"
                  render={({ field }) => (
                    <SearchCombobox
                      id="alocacao-contract"
                      placeholder={t('alocacao:form.contratoPlaceholder')}
                      value={field.value ?? null}
                      onChange={(option) => {
                        const previous = getValues('contract');
                        field.onChange(option);
                        applyContract(option, previous);
                      }}
                      useSearch={useAllocatableContracts}
                      getLabel={contractLabel}
                      invalid={!!errors.contract}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
            </div>

            {/* Linha 3 — Projeto · Perfil de venda (ambos dependem do contrato; perfil só em projeto que fatura). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                id="alocacao-project"
                label={t('alocacao:form.projeto')}
                hint={
                  !selectedContractId
                    ? t('alocacao:form.projetoSemContrato')
                    : !billsProject
                      ? t('alocacao:form.projetoSemFaturamento')
                      : undefined
                }
                hintMode={selectedContractId ? 'tooltip' : 'inline'}
                error={projectError}
                required
                className={billsProject ? undefined : 'md:col-span-2'}
              >
                <Controller
                  control={control}
                  name="project"
                  render={({ field }) => (
                    <SearchCombobox
                      id="alocacao-project"
                      placeholder={t('alocacao:form.projetoPlaceholder')}
                      value={field.value ? { id: field.value.id, name: field.value.name } : null}
                      onChange={(_option, item) => {
                        field.onChange(item);
                        if (item) applyProjectRules(item);
                      }}
                      useSearch={useProjectsOfContract}
                      getLabel={(p) => `${p.axisCode} - ${p.name}`}
                      invalid={!!projectError}
                      disabled={readOnly || !selectedContractId}
                    />
                  )}
                />
              </FormField>
              {billsProject && (
                <FormField
                  id="alocacao-sales-profile"
                  label={t('alocacao:form.perfilVenda')}
                  hint={selectedContractId ? undefined : t('alocacao:form.perfilVendaSemContrato')}
                  error={serverErrorFor('salesProfileId')}
                >
                  <Controller
                    control={control}
                    name="salesProfileId"
                    render={({ field }) => (
                      <SalesProfileSelect
                        id="alocacao-sales-profile"
                        profiles={salesProfiles}
                        value={field.value ?? null}
                        currentLabel={
                          target?.allocation.salesProfile?.id === field.value
                            ? target?.allocation.salesProfile?.name
                            : undefined
                        }
                        onChange={applySalesProfile}
                        disabled={readOnly || !selectedContractId}
                        invalid={!!serverErrorFor('salesProfileId')}
                      />
                    )}
                  />
                </FormField>
              )}
            </div>

            {/* Linha 4 — custo: moeda · custo mês · % alocado · custo alocado. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <FormField label={t('alocacao:form.custoEm')}>
                <Input disabled value={costIso ?? ''} />
              </FormField>
              <FormField
                id="alocacao-monthly-cost"
                label={t('alocacao:form.custoMes')}
                hint={t('alocacao:form.custoMesHint')}
                hintMode="tooltip"
                error={valueError('monthlyCost')}
              >
                <Controller
                  control={control}
                  name="monthlyCost"
                  render={({ field }) => (
                    <NumberInput
                      id="alocacao-monthly-cost"
                      value={field.value}
                      onChange={field.onChange}
                      min={0}
                      precision={2}
                      showControls={false}
                      {...decimalFormat}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="alocacao-percentage"
                label={t('alocacao:form.percAlocado')}
                error={
                  serverErrorFor('allocatedPercentage') ??
                  (errors.allocatedPercentage ? t('alocacao:erros.invalidPercentage') : undefined)
                }
                required
              >
                <Controller
                  control={control}
                  name="allocatedPercentage"
                  render={({ field }) => (
                    <NumberInput
                      id="alocacao-percentage"
                      value={field.value}
                      onChange={field.onChange}
                      min={1}
                      max={100}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
              <FormField label={t('alocacao:form.custoAlocado')}>
                <Input
                  disabled
                  value={formatMoney(
                    previewAllocatedCost(values.monthlyCost, values.allocatedPercentage),
                    costIso,
                    i18n.language,
                  )}
                />
              </FormField>
            </div>

            {/* Linha 5 (só projeto de pool) — aviso do pool: avisado · avisado em · número de dias. */}
            {selectedProject?.isPool && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <FormField id="alocacao-notified" label={t('alocacao:form.avisado')}>
                  <Controller
                    control={control}
                    name="notified"
                    render={({ field }) => (
                      <Switch
                        id="alocacao-notified"
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          applyNotice();
                        }}
                        disabled={readOnly}
                      />
                    )}
                  />
                </FormField>
                <FormField
                  id="alocacao-notice-date"
                  label={t('alocacao:form.avisadoEm')}
                  error={
                    serverErrorFor('noticeDate') ??
                    (errors.noticeDate ? t('common:errors.required') : undefined)
                  }
                  required={values.notified}
                >
                  <Controller
                    control={control}
                    name="noticeDate"
                    render={({ field }) => (
                      <DatePicker
                        id="alocacao-notice-date"
                        value={field.value}
                        onChange={(date) => {
                          field.onChange(date ?? null);
                          applyNotice();
                        }}
                        clearable
                        disabled={readOnly || !values.notified}
                      />
                    )}
                  />
                </FormField>
                <FormField
                  id="alocacao-notice-days"
                  label={t('alocacao:form.numeroDias')}
                  error={serverErrorFor('noticeDays')}
                >
                  <Controller
                    control={control}
                    name="noticeDays"
                    render={({ field }) => (
                      <NumberInput
                        id="alocacao-notice-days"
                        value={field.value}
                        onChange={(days) => {
                          field.onChange(days);
                          applyNotice();
                        }}
                        min={0}
                        disabled={readOnly || !values.notified}
                      />
                    )}
                  />
                </FormField>
              </div>
            )}

            {/* Linha 6 (só projeto que fatura) — preço: moeda · preço hora · horas vendidas · faturamento bruto · faturamento líquido. */}
            {billsProject && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <FormField label={t('alocacao:form.precoEm')}>
                  <Input disabled value={billingIso ?? ''} />
                </FormField>
                <FormField
                  id="alocacao-hourly-rate"
                  label={t('alocacao:form.precoHora')}
                  error={valueError('hourlyRate')}
                >
                  <Controller
                    control={control}
                    name="hourlyRate"
                    render={({ field }) => (
                      <NumberInput
                        id="alocacao-hourly-rate"
                        value={field.value}
                        onChange={field.onChange}
                        min={0}
                        precision={2}
                        showControls={false}
                        {...decimalFormat}
                        disabled={readOnly || !billsProject}
                      />
                    )}
                  />
                </FormField>
                <FormField
                  id="alocacao-sold-hours"
                  label={t('alocacao:form.horasVendidas')}
                  error={valueError('soldHours')}
                >
                  <Controller
                    control={control}
                    name="soldHours"
                    render={({ field }) => (
                      <NumberInput
                        id="alocacao-sold-hours"
                        value={field.value}
                        onChange={field.onChange}
                        min={0}
                        precision={2}
                        showControls={false}
                        {...decimalFormat}
                        disabled={readOnly || !billsProject}
                      />
                    )}
                  />
                </FormField>
                <FormField label={t('alocacao:form.fatBrutoMes')}>
                  <Input
                    disabled
                    value={formatMoney(
                      billsProject ? previewGrossRevenue(values.hourlyRate, values.soldHours) : 0,
                      billingIso,
                      i18n.language,
                    )}
                  />
                </FormField>
                <FormField label={t('alocacao:form.fatLiquidoMes')}>
                  <Input
                    disabled
                    value={formatMoney(
                      billsProject
                        ? previewNetRevenue(
                            previewGrossRevenue(values.hourlyRate, values.soldHours),
                            selectedProfile?.taxRate,
                          )
                        : 0,
                      billingIso,
                      i18n.language,
                    )}
                  />
                </FormField>
              </div>
            )}

            {/* Linha 7 — rateio BR · rateio US · início · fim; fim confirmado logo abaixo. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <FormField
                id="alocacao-cost-share-br"
                label={t('alocacao:form.rateioCustoBr')}
                error={serverErrorFor('costShareBr')}
                required
              >
                <Controller
                  control={control}
                  name="costShareBr"
                  render={({ field }) => (
                    <NumberInput
                      id="alocacao-cost-share-br"
                      value={field.value}
                      onChange={field.onChange}
                      min={0}
                      max={100}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="alocacao-cost-share-us"
                label={t('alocacao:form.rateioCustoUs')}
                error={
                  errors.costShareUs?.message
                    ? t('alocacao:form.rateioSomaErro')
                    : serverErrorFor('costShareUs')
                }
                required
              >
                <Controller
                  control={control}
                  name="costShareUs"
                  render={({ field }) => (
                    <NumberInput
                      id="alocacao-cost-share-us"
                      value={field.value}
                      onChange={field.onChange}
                      min={0}
                      max={100}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="alocacao-start-date"
                label={t('alocacao:form.dtInicio')}
                error={
                  serverErrorFor('startDate') ??
                  (errors.startDate ? t('common:errors.required') : undefined)
                }
                required
              >
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <DatePicker
                      id="alocacao-start-date"
                      value={field.value}
                      onChange={(d) => field.onChange(d as Date)}
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="alocacao-end-date"
                label={t('alocacao:form.dtFim')}
                hint={
                  selectedProject?.isPool && values.notified
                    ? t('alocacao:form.fimPeloAviso')
                    : t('alocacao:form.dtFimHint')
                }
                hintMode="tooltip"
                error={
                  serverErrorFor('endDate') ??
                  (errors.endDate?.message ? t('alocacao:erros.endDateBeforeStartDate') : undefined)
                }
              >
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <DatePicker
                      id="alocacao-end-date"
                      value={field.value}
                      onChange={field.onChange}
                      clearable
                      disabled={readOnly}
                    />
                  )}
                />
              </FormField>
            </div>

            <Controller
              control={control}
              name="fimConfirmado"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  label={t('alocacao:form.fimConfirmado')}
                  disabled={readOnly}
                />
              )}
            />

            {/* Linha 8 — Anotações. */}
            <FormField
              label={t('alocacao:form.anotacoes')}
              error={
                errors.notes
                  ? t('alocacao:form.anotacoesLimite', { max: NOTES_MAX_LENGTH })
                  : undefined
              }
            >
              <Textarea
                {...register('notes')}
                rows={3}
                maxLength={NOTES_MAX_LENGTH}
                disabled={readOnly}
              />
            </FormField>
          </div>
        </ScrollArea>
      </Modal.Body>

      <Modal.Footer className="border-t border-border">
        {readOnly ? (
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common:close')}
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t('common:cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSave}
              loading={saveMutation.isPending}
            >
              {target ? t('common:save') : t('alocacao:form.alocar')}
            </Button>
          </>
        )}
      </Modal.Footer>
    </form>
  );
}

export function AllocationModal({
  open,
  onClose,
  target,
  today,
  readOnly = false,
}: AllocationModalProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const titleId = useId();
  const formRef = useRef<FormBodyHandle>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  function close() {
    setConfirmingDiscard(false);
    onClose();
  }

  function requestClose() {
    if (formRef.current?.isDirty && !readOnly) setConfirmingDiscard(true);
    else close();
  }

  const title = readOnly
    ? t('alocacao:form.visualizarTitulo')
    : target
      ? t('alocacao:form.editarTitulo')
      : t('alocacao:form.novaTitulo');
  return (
    <Modal
      open={open}
      onClose={requestClose}
      closeOnBackdrop={false}
      size="4xl"
      ariaLabelledBy={titleId}
    >
      <Modal.Header
        titleId={titleId}
        title={title}
        onClose={requestClose}
        dismissLabel={t('common:close')}
      />
      {open && (
        <FormBody
          key={target?.allocation.id ?? 'nova'}
          target={target}
          today={today}
          onClose={close}
          onCancel={requestClose}
          ref={formRef}
          readOnly={readOnly}
        />
      )}
      <AlertDialog
        open={confirmingDiscard}
        onOpenChange={(next) => !next && setConfirmingDiscard(false)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>{t('alocacao:form.descartarTitulo')}</AlertDialog.Title>
          <AlertDialog.Description>{t('alocacao:form.descartarDescricao')}</AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel type="button">
              {t('alocacao:form.continuarEditando')}
            </AlertDialog.Cancel>
            <AlertDialog.Action type="button" variant="danger" onClick={close}>
              {t('alocacao:form.descartar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </Modal>
  );
}
